import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@web/lib/cn';

export type ButtonTone = 'primary' | 'quiet' | 'danger';

/**
 * The only button in the console.
 *
 * Square, hairline-bounded, no shadow, no gradient. The 44px minimum on the
 * `touch` size is Apple's HIG figure rather than WCAG's 24px, because the two
 * buttons that matter here — Claim and Take over — are pressed under time
 * pressure on a phone, and a mis-tap on a first-wins action is an incident, not
 * an annoyance.
 */
export function Button({
  tone = 'quiet',
  size = 'default',
  className,
  children,
  ...rest
}: ComponentPropsWithRef<'button'> & {
  tone?: ButtonTone;
  size?: 'default' | 'touch' | 'small';
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        'inline-flex items-center justify-center gap-2 border font-medium whitespace-nowrap transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45',
        size === 'small' && 'h-6 px-2 text-[11px]',
        size === 'default' && 'h-8 px-3 text-xs',
        size === 'touch' && 'min-h-11 min-w-11 px-4 text-sm',
        tone === 'primary' && 'border-ink bg-ink text-ground hover:bg-ink/85 disabled:hover:bg-ink',
        tone === 'quiet' &&
          'border-rule-strong bg-transparent text-ink hover:bg-hover disabled:hover:bg-transparent',
        tone === 'danger' &&
          'border-signal bg-signal/12 text-signal hover:bg-signal/20 disabled:hover:bg-signal/12',
        className,
      )}
    >
      {children}
    </button>
  );
}
